import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Wrench, Droplet, Award, Globe, Recycle } from 'lucide-react';
import Counter from './Counter';
import { Reveal } from './Reveal';

/**
 * GuaranteesStrip — Premium editorial layout with large visual cards
 * Redesigned for maximum impact and luxury aesthetic
 */
const ITEMS = [
  { 
    icon: ShieldCheck, 
    num: 10,  
    numSuffix: '-Year',   
    line2: 'Product Guarantee',
    desc: 'Full coverage on all grass products'
  },
  { 
    icon: Wrench,      
    num: 24,  
    numSuffix: '-Month',  
    line2: 'Groundworks Guarantee',
    desc: 'Sub-base and drainage protection'
  },
  { 
    icon: Droplet,     
    text: 'SUDS',                   
    line2: 'Compliant Drainage',
    desc: 'Sustainable urban drainage systems'
  },
  { 
    icon: Award,       
    num: 20,  
    numSuffix: '+ Years', 
    line2: 'Experience',
    desc: 'Specialist groundworks heritage'
  },
  { 
    icon: Globe,       
    text: 'European',               
    line2: 'Manufactured',
    desc: 'Premium EU quality standards'
  },
  { 
    icon: Recycle,     
    text: 'Fully',                  
    line2: 'Recyclable',
    desc: '100% environmentally responsible'
  },
];

export default function GuaranteesStrip({ compact = false }) {
  if (compact) {
    // Compact footer version — horizontal animated marquee on desktop,
    // wrapping pill layout on mobile. Continuously scrolling band reinforces
    // the brand's six promises right above the footer.
    const DUPLICATED = [...ITEMS, ...ITEMS]; // duplicate so the loop is seamless
    // Split into two rows so mobile can show one going left and one going right
    const ROW_A = DUPLICATED;
    const ROW_B = [...DUPLICATED].reverse();
    return (
      <section className="relative bg-[#E7E9E4] border-y border-[#1A1A1A]/8 py-5 md:py-6 overflow-hidden" data-testid="guarantees-strip">
        {/* MOBILE — TWO rows scrolling in OPPOSITE directions */}
        <div className="md:hidden relative">
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-[#E7E9E4] to-transparent z-10" />
          <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-[#E7E9E4] to-transparent z-10" />

          <div className="overflow-hidden mb-2">
            <motion.div
              className="flex items-center gap-7 whitespace-nowrap will-change-transform"
              animate={{ x: ['0%', '-50%'] }}
              transition={{ duration: 32, ease: 'linear', repeat: Infinity }}
            >
              {ROW_A.map((it, i) => {
                const Icon = it.icon;
                const hasNumber = typeof it.num === 'number';
                return (
                  <div key={i} className="flex items-center gap-2 text-[#1A1A1A]">
                    <Icon size={14} className="text-[#6A8F3E]" strokeWidth={1.8} />
                    <span className="text-[12px] tracking-wide">
                      <span className="font-bold">{hasNumber ? `${it.num}${it.numSuffix}` : it.text}</span>
                      <span className="opacity-75 ml-1">{it.line2}</span>
                    </span>
                  </div>
                );
              })}
            </motion.div>
          </div>
          <div className="overflow-hidden">
            <motion.div
              className="flex items-center gap-7 whitespace-nowrap will-change-transform"
              animate={{ x: ['-50%', '0%'] }}
              transition={{ duration: 32, ease: 'linear', repeat: Infinity }}
            >
              {ROW_B.map((it, i) => {
                const Icon = it.icon;
                const hasNumber = typeof it.num === 'number';
                return (
                  <div key={i} className="flex items-center gap-2 text-[#1A1A1A]">
                    <Icon size={14} className="text-[#6A8F3E]" strokeWidth={1.8} />
                    <span className="text-[12px] tracking-wide">
                      <span className="font-bold">{hasNumber ? `${it.num}${it.numSuffix}` : it.text}</span>
                      <span className="opacity-75 ml-1">{it.line2}</span>
                    </span>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>

        {/* DESKTOP — continuously scrolling marquee */}
        <div
          className="hidden md:block relative"
          aria-roledescription="marquee"
          aria-label="Guardian guarantees"
        >
          {/* fade edges */}
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#E7E9E4] to-transparent z-10" />
          <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#E7E9E4] to-transparent z-10" />

          <div className="overflow-hidden">
            <motion.div
              className="flex items-center gap-12 whitespace-nowrap will-change-transform"
              animate={{ x: ['0%', '-50%'] }}
              transition={{ duration: 38, ease: 'linear', repeat: Infinity }}
            >
              {DUPLICATED.map((it, i) => {
                const Icon = it.icon;
                const hasNumber = typeof it.num === 'number';
                return (
                  <div key={i} className="flex items-center gap-3 text-[#1A1A1A]">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#6A8F3E]/10 border border-[#6A8F3E]/25">
                      <Icon size={16} className="text-[#6A8F3E]" strokeWidth={1.8} />
                    </span>
                    <span className="text-[14px] tracking-wide">
                      <span className="font-bold text-[#1A1A1A]">
                        {hasNumber ? `${it.num}${it.numSuffix}` : it.text}
                      </span>
                      <span className="text-[#1A1A1A]/75 ml-2">{it.line2}</span>
                    </span>
                    {i !== DUPLICATED.length - 1 && (
                      <span aria-hidden className="ml-12 w-1 h-1 rounded-full bg-[#6A8F3E]/40" />
                    )}
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>
    );
  }

  // Full premium version with editorial cards
  return (
    <section className="relative bg-[#E7E9E4] py-20 md:py-32 overflow-hidden" data-testid="guarantees-strip">
      {/* Subtle ambient glow — dual brand-green wash (was an orange/gold tint) */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-[#6A8F3E]/5 blur-[160px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full bg-[#314F2B]/6 blur-[160px] pointer-events-none" />

      <div className="mx-auto px-6 md:px-12 relative" style={{ maxWidth: '1700px' }}>
        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <Reveal>
            <div className="flex items-center gap-3 justify-center mb-6">
              <span className="w-12 h-px bg-[#6A8F3E]/30" />
              <span className="text-[10px] tracking-[0.45em] uppercase text-[#1A1A1A]/60 font-semibold">
                Built to last · Promise to perform
              </span>
              <span className="w-12 h-px bg-[#6A8F3E]/30" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif-editorial text-3xl md:text-5xl lg:text-6xl leading-tight tracking-tight text-[#1A1A1A]">
              Engineering-backed
              <span className="block italic text-[#6A8F3E] mt-2">guarantees.</span>
            </h2>
          </Reveal>
        </div>

        {/* Premium Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {ITEMS.map((it, i) => {
            const Icon = it.icon;
            const hasNumber = typeof it.num === 'number';
            
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="group"
              >
                <div className="relative h-full bg-white rounded-2xl p-8 md:p-10 shadow-lg hover:shadow-xl transition-all duration-500 border border-[#1A1A1A]/8 hover:-translate-y-1">
                  {/* Icon */}
                  <div className="mb-6">
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: i * 0.1 + 0.2 }}
                      className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#6A8F3E]/10 border border-[#6A8F3E]/20 group-hover:bg-[#6A8F3E]/15 group-hover:border-[#6A8F3E]/30 transition-all duration-500"
                    >
                      <Icon size={28} className="text-[#6A8F3E]" strokeWidth={1.6} />
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div>
                    {/* Main Value */}
                    <div className="mb-3">
                      {hasNumber ? (
                        <div className="font-serif-editorial text-5xl md:text-6xl leading-none tracking-tight text-[#1A1A1A]">
                          <Counter value={it.num} className="tabular-nums" />
                          <span className="text-[#6A8F3E]">{it.numSuffix}</span>
                        </div>
                      ) : (
                        <div className="font-serif-editorial text-5xl md:text-6xl leading-none tracking-tight">
                          <span className="text-[#1A1A1A]">{it.text}</span>
                        </div>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="font-sans-brand uppercase text-sm tracking-[0.2em] font-bold text-[#1A1A1A] mb-3">
                      {it.line2}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-[#1A1A1A]/65 leading-relaxed">
                      {it.desc}
                    </p>
                  </div>

                  {/* Decorative corner accent */}
                  <div className="absolute top-0 right-0 w-20 h-20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                    <svg width="100%" height="100%" viewBox="0 0 80 80" fill="none">
                      <path d="M 80 0 L 80 40 Q 80 80, 40 80 L 0 80" stroke="#6A8F3E" strokeWidth="1" opacity="0.2" />
                    </svg>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Optional bottom note */}
        <Reveal delay={0.4}>
          <p className="text-center mt-12 text-sm text-[#1A1A1A]/50 max-w-2xl mx-auto leading-relaxed">
            All guarantees backed by Guardian Builders Ltd, a company with extensive civil engineering experience and a proven track record across London and the South East.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
